import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight, Info, BadgeCheck } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { BeforeAfter } from "../components/BeforeAfter";
import { PROJECTS, PROJECT_CATEGORIES } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (path) => `${API}/files/${path}`;

export default function Projects() {
  const [cat, setCat] = useState("All");
  const [genuine, setGenuine] = useState([]);

  useEffect(() => {
    document.title = "Projects | KAC — Krishnaa Constructions";
    axios.get(`${API}/projects`).then((r) => setGenuine(r.data)).catch(() => {});
  }, []);

  const hasGenuine = genuine.length > 0;
  const cats = hasGenuine
    ? ["All", ...PROJECT_CATEGORIES.filter((c) => c !== "All" && genuine.some((p) => p.category === c))]
    : PROJECT_CATEGORIES;
  const list = hasGenuine
    ? cat === "All" ? genuine : genuine.filter((p) => p.category === cat)
    : cat === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === cat);
  const baPair = genuine.find((p) => p.before_path);

  return (
    <>
      <section data-testid="projects-hero" className="bg-brand-green py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-white/70">Projects</p>
            <h1 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
              {hasGenuine ? "Our work, in our customers' homes." : "The kind of work we do."}
            </h1>
            <p className="mt-5 max-w-2xl leading-relaxed text-white/85">
              Browse {hasGenuine ? "genuine KAC projects" : "examples of the projects KAC undertakes"} — loft conversions,
              extensions, kitchens, bathrooms, tiling and general building work.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {!hasGenuine && (
            <Reveal>
              <div data-testid="placeholder-notice" className="flex items-start gap-3 rounded-2xl border border-brand-red/25 bg-brand-red/5 p-5 text-sm text-brand-ink/80">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                <p>
                  <strong className="text-brand-red">Please note:</strong> the images below are illustrative placeholders, not
                  photographs of completed KAC projects. Genuine photos of our finished work will be added here soon.
                </p>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.1} className="mt-10">
            <div data-testid="project-filters" className="flex flex-wrap gap-2.5">
              {cats.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all active:scale-95 ${
                    cat === c ? "bg-brand-green text-white shadow-md" : "bg-brand-light text-brand-green hover:bg-brand-green/15"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <article data-testid={`project-card-${p.id}`} className="group h-full overflow-hidden rounded-2xl border border-brand-light bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={hasGenuine ? fileUrl(p.image_path) : p.image}
                      alt={hasGenuine ? `${p.title} — genuine KAC project in ${p.location || "the UK"}` : `${p.title} — illustrative placeholder image`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-brand-green px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">{p.category}</span>
                    {hasGenuine ? (
                      <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-white/95 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-green">
                        <BadgeCheck className="h-3.5 w-3.5" /> Genuine KAC work
                      </span>
                    ) : (
                      <span className="absolute bottom-3 right-3 rounded-full bg-brand-ink/60 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">Sample image</span>
                    )}
                  </div>
                  <div className="p-6">
                    <h2 className="font-heading text-lg font-bold text-brand-green">{p.title}</h2>
                    {p.location && (
                      <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-muted">
                        <MapPin className="h-3.5 w-3.5 text-brand-red" /> {p.location}
                      </p>
                    )}
                    <p className="mt-3 text-sm leading-relaxed text-brand-muted">{p.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-green2">Before & after</p>
                <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-brand-green">See the transformation.</h2>
                <p className="mt-4 leading-relaxed text-brand-muted">
                  {baPair
                    ? `Drag the slider to compare a genuine KAC transformation${baPair.location ? ` in ${baPair.location}` : ""}.`
                    : "Drag the slider to compare. This before-and-after viewer is ready for genuine KAC project photography — placeholder imagery is shown for now."}
                </p>
                <Link to="/contact" data-testid="projects-quote-btn" className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-red px-7 py-3.5 font-bold text-white shadow-lg transition-all hover:bg-brand-redDark active:scale-95">
                  Start your project <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <BeforeAfter
                before={baPair ? fileUrl(baPair.before_path) : undefined}
                after={baPair ? fileUrl(baPair.image_path) : undefined}
                genuine={Boolean(baPair)}
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
